import { useEntitiesState } from "../../hooks/useEntitiesState";
import { useDoc } from "@poseidon/firebase/hooks/useDoc";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import { getSchemaErrors } from "@poseidon/utils/yup";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";

const rowDefaults = () => ({
  toAmount: "",
  fromAmount: "",
  minMonths: "",
  maxMonths: "",
});

const sorter = (rows) => {
  return [...rows]
    .sort((a, b) => parseInt(a.toAmount) - parseInt(b.toAmount))
    .map((row, idx, all) => {
      const toAmount = idx > 0 && all[idx - 1].toAmount;
      const fromAmount =
        idx === 0 ? "0" : toAmount ? parseInt(toAmount) + 1 : "";
      return {
        ...row,
        fromAmount,
      };
    });
};

const mapForSave = (rows) => {
  return rows.map(({ id, rows, ...row }) => {
    return {
      fromAmount: parseInt(row.fromAmount) ?? 0,
      toAmount: parseInt(row.toAmount) ?? 0,
      minMonths: parseInt(row.minMonths) ?? 0,
      maxMonths: parseInt(row.maxMonths) ?? 0,
    };
  });
};

const rowSchema = Yup.object().shape({
  fromAmount: Yup.number()
    .typeError("Invalid number")
    .required()
    .min(0, "Must be positive")
    .lessThan(Yup.ref("toAmount"), 'Must be less than "To Amount"'),
  toAmount: Yup.number()
    .typeError("Invalid number")
    .required()
    .positive("Must be more than 0")
    .moreThan(Yup.ref("fromAmount"), 'Must be more than "From Amount"'),
  minMonths: Yup.number()
    .typeError("Invalid number")
    .required()
    .min(1, "Must be more than 0")
    .lessThan(Yup.ref("maxMonths"), 'Must be less than "To Months"'),
  maxMonths: Yup.number()
    .typeError("Invalid number")
    .required()
    .min(1, "Must be more than 0")
    .moreThan(Yup.ref("minMonths"), 'Must be more than "From Months"'),
});

const validate = (rows) => {
  const errors = rows
    .map((row) => {
      const schemaErrors = getSchemaErrors(rowSchema, row);
      if (schemaErrors) {
        return {
          id: row.id,
          fieldErrors: schemaErrors,
        };
      }
      return undefined;
    })
    .filter((v) => !!v);
  return errors.length ? errors : undefined;
};

export const useTermLimitsState = () => {
  const { setToast } = useToastMessage();
  const { setError } = useErrorMessage();
  const { data, loading, loadError, saveError, saving, update } = useDoc(
    "matrix",
    "limits"
  );
  const [validationErrors, setValidationErrors] = useState();

  const { rows, actions } = useEntitiesState({
    nested: false,
    defaults: rowDefaults,
    sorter,
  });
  useEffect(() => {
    if (data) {
      actions.init(data.rules);
    }
  }, [actions, data]);
  const handleSave = () => {
    const vErr = validate(rows);
    if (!vErr) {
      setValidationErrors(undefined);
      update({ rules: mapForSave(rows) }).then((success) => {
        if (success) {
          setToast("Successfully saved term limits");
        } else {
          setError("Error saving rate matrix");
        }
      });
    } else {
      setValidationErrors(vErr);
    }
  };
  return {
    rows,
    actions,
    loading,
    loadError,
    saveError,
    saving,
    handleSave,
    validationErrors,
  };
};
